import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import AgentConfigEditor from './AgentConfigEditor';
import KnowledgeBaseManager from './KnowledgeBaseManager';
import { apiFetch } from '../utils/apiClient';

const Settings = ({ isOpen, onClose, personas, onUpdatePersona, onAgentsChanged }) => {
  const [tab, setTab] = useState('agents'); // 'agents' | 'knowledge-bases'
  const [view, setView] = useState('list'); // 'list' | 'edit' | 'config'
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingPersona, setEditingPersona] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    instructions: '',
    role: ''
  });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newAgentData, setNewAgentData] = useState({
    id: '',
    name: '',
    description: '',
    icon: '🤖',
    system_prompt: ''
  });

  // Load agents list when modal opens
  useEffect(() => {
    if (isOpen) {
      loadAgents();
    }
  }, [isOpen]);

  // Load agents list when modal opens
  useEffect(() => {
    if (isOpen) {
      loadAgents();
    }
  }, [isOpen]);

  const loadAgents = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch('/api/agents');
      if (!response.ok) throw new Error('Failed to load agents');
      const data = await response.json();
      setAgents(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAgent = async () => {
    if (!newAgentData.id || !newAgentData.name) {
      alert('Please provide agent ID and name');
      return;
    }

    setLoading(true);
    try {
      const response = await apiFetch('/api/agents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newAgentData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create agent');
      }

      alert(`✓ Agent '${newAgentData.name}' created successfully`);
      setShowCreateForm(false);
      setNewAgentData({ id: '', name: '', description: '', icon: '🤖', system_prompt: '' });
      await loadAgents();
      // Notify parent to refresh PersonaSelector
      if (onAgentsChanged) onAgentsChanged();
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAgent = async (agentId) => {
    if (!confirm(`Are you sure you want to delete agent '${agentId}'? This cannot be undone.`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await apiFetch(`/api/agents/${agentId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete agent');
      }

      alert(`✓ Agent '${agentId}' deleted successfully`);
      await loadAgents();
      // Notify parent to refresh PersonaSelector
      if (onAgentsChanged) onAgentsChanged();
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleConfigureAgent = (agentId) => {
    setSelectedAgentId(agentId);
    setView('config');
  };

  const handleCloseConfig = () => {
    setView('list');
    setSelectedAgentId(null);
    loadAgents(); // Reload list in case config changed
  };

  const defaultPersonas = {
    PO: {
      name: 'Product Owner',
      description: 'Product vision, strategy, and market alignment',
      role: 'Product Manager',
      instructions: `You are a Product Manager focused on:
• Define product vision, strategy, and roadmap
• Conduct market research and competitive analysis
• Gather and prioritize customer requirements
• Align product goals with business objectives
• Collaborate with stakeholders to ensure product-market fit
• Own product lifecycle management (concept → launch → iteration)
• Measure product success using KPIs and feedback loops

Your communication style should be business-focused, user-centric, and strategic.`
    },
    Architect: {
      name: 'Software Architect',
      description: 'Technical vision and system design',
      role: 'Software Architect',
      instructions: `You are a Software Architect focused on:
• Establish technical vision and system design principles
• Define standards for scalability, security, and performance
• Create and maintain architectural blueprints and documentation
• Ensure alignment between business needs and technical solutions
• Evaluate and recommend technology stacks and frameworks
• Govern integration patterns across systems and platforms
• Provide guidance on long-term technical evolution and innovation

Your communication style should be technical, systematic, and design-oriented.`
    },
    Engineer: {
      name: 'Software Engineer',
      description: 'Implementation and code quality',
      role: 'Software Engineer',
      instructions: `You are a Software Engineer focused on:
• Implement product features based on requirements and architecture
• Write clean, maintainable, and testable code
• Ensure adherence to coding standards and best practices
• Conduct unit, integration, and system testing
• Manage CI/CD pipelines and deployment processes
• Monitor system performance and resolve technical issues
• Collaborate with product and architecture teams for delivery alignment
• Drive continuous improvement in engineering practices

Your communication style should be practical, detail-oriented, and implementation-focused.`
    }
  };

  const startEditing = (personaId) => {
    const persona = personas[personaId] || defaultPersonas[personaId];
    setEditingPersona(personaId);
    setFormData({
      name: persona.name,
      description: persona.description,
      instructions: persona.instructions,
      role: persona.role
    });
  };

  const handleSave = () => {
    if (editingPersona && onUpdatePersona) {
      onUpdatePersona(editingPersona, formData);
    }
    setEditingPersona(null);
    setFormData({ name: '', description: '', instructions: '', role: '' });
  };

  const handleCancel = () => {
    setEditingPersona(null);
    setFormData({ name: '', description: '', instructions: '', role: '' });
  };

  const resetToDefault = (personaId) => {
    if (onUpdatePersona) {
      onUpdatePersona(personaId, defaultPersonas[personaId]);
    }
  };

  if (!isOpen) return null;

  // Show AgentConfigEditor view
  if (view === 'config' && selectedAgentId) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          <AgentConfigEditor 
            agentId={selectedAgentId} 
            onClose={handleCloseConfig}
            embedded={true}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <h2 className="text-xl font-semibold text-gray-800">Settings</h2>
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  setTab('agents');
                  setView('list');
                }}
                className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                  tab === 'agents' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                🤖 Agents ({agents.length})
              </button>
              <button
                onClick={() => setTab('knowledge-bases')}
                className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                  tab === 'knowledge-bases' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                📚 Knowledge Bases
              </button>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {tab === 'knowledge-bases' ? (
            <KnowledgeBaseManager embedded={true} isVisible={tab === 'knowledge-bases'} />
          ) : (
            <>
              {loading && (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading agents...</p>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                  Error: {error}
                </div>
              )}

              {!loading && !error && (
                <>
                  {/* Create Agent Button */}
                  <div className="mb-6">
                    <button
                      onClick={() => setShowCreateForm(!showCreateForm)}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2"
                    >
                      <span>{showCreateForm ? '✕ Cancel' : '➕ Create New Agent'}</span>
                </button>
              </div>

              {/* Create Agent Form */}
              {showCreateForm && (
                <div className="mb-6 p-6 bg-blue-50 border border-blue-200 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Create New Agent</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Agent ID* (lowercase, no spaces)
                      </label>
                      <input
                        type="text"
                        value={newAgentData.id}
                        onChange={(e) => setNewAgentData(prev => ({ ...prev, id: e.target.value.toLowerCase().replace(/\s/g, '-') }))}
                        placeholder="e.g., data-analyst"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Name*
                      </label>
                      <input
                        type="text"
                        value={newAgentData.name}
                        onChange={(e) => setNewAgentData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="e.g., Data Analyst"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Icon (emoji)
                      </label>
                      <input
                        type="text"
                        value={newAgentData.icon}
                        onChange={(e) => setNewAgentData(prev => ({ ...prev, icon: e.target.value }))}
                        placeholder="🤖"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <input
                        type="text"
                        value={newAgentData.description}
                        onChange={(e) => setNewAgentData(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="e.g., Data analysis and insights expert"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      System Prompt
                    </label>
                    <textarea
                      value={newAgentData.system_prompt}
                      onChange={(e) => setNewAgentData(prev => ({ ...prev, system_prompt: e.target.value }))}
                      rows={4}
                      placeholder="Define the agent's role, expertise, and behavior..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <button
                    onClick={handleCreateAgent}
                    disabled={loading || !newAgentData.id || !newAgentData.name}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    Create Agent
                  </button>
                </div>
              )}

              {/* Agents List */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Existing Agents</h3>
                {agents.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No agents found. Create one to get started!</p>
                ) : (
                  agents.map((agentId) => (
                    <div key={agentId} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                      <div className="flex justify-between items-center">
                        <div>
                          <h4 className="text-lg font-medium text-gray-800">{agentId}</h4>
                          <p className="text-sm text-gray-600">Click configure to view details</p>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleConfigureAgent(agentId)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                          >
                            ⚙️ Configure
                          </button>
                          <button
                            onClick={() => handleDeleteAgent(agentId)}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

Settings.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  personas: PropTypes.object.isRequired,
  onUpdatePersona: PropTypes.func.isRequired,
  onAgentsChanged: PropTypes.func
};

export default Settings;